/**
 * When you follow a link, the page you asked for is the thing you should see
 * (owner ask, Miles 2026-09-20).
 *
 * Clicking an in-app link used to navigate underneath a chat that was still
 * covering the page — the route changed, the thread stayed live, and the
 * person saw no difference. The chat now steps aside: the docked right rail on
 * a desktop, the corner launcher on a phone.
 *
 * THE MIDDLE IS DECIDED BY ROOM, NOT BY A NEW BREAKPOINT. "Dynamically chosen
 * strategically when between the two" is `DOCKED_COMPANION_MIN_VIEWPORT_PX` —
 * the repo's existing single answer to "is there space to share the screen
 * with the docked rail?", the same number App's RoutedContentShell reflows the
 * page on. Below it a docked pane would leave the page under 400px, so the
 * chat minimises instead of pretending to share.
 *
 * Pure, and it decides nothing about WHEN it is asked: the callers are the
 * places a navigation actually happens (see GlobalChatSystem). Deliberately
 * not wired to route changes — the chat's navPath tracker cannot tell a link
 * from a back button or a boot-time redirect, and stepping aside for those is
 * not what was asked for.
 */
import { DOCKED_COMPANION_MIN_VIEWPORT_PX } from './dockedCompanion';

export type NavigationReveal = 'none' | 'dock' | 'minimize';

export interface NavigationRevealInput {
  /** The pane is open. A closed chat is already out of the way. */
  open: boolean;
  /** The surface suppresses the chat entirely (config.hidden). */
  hidden: boolean;
  fullScreen: boolean;
  docked: boolean;
  /** This surface has no docked mode to step into. */
  dockingDisabled: boolean;
  /** window.innerWidth — what the companion split is measured against. */
  viewportWidth: number;
}

export function revealForNavigation(input: NavigationRevealInput): NavigationReveal {
  if (!input.open || input.hidden) return 'none';
  // Already the side rail: the page is beside it, which is the whole goal.
  if (input.docked && !input.fullScreen) return 'none';
  // Everything else covers the page — the fullscreen takeover obviously, and
  // the centred floating pane, which sits over the middle of it.
  if (input.dockingDisabled) return 'minimize';
  return input.viewportWidth >= DOCKED_COMPANION_MIN_VIEWPORT_PX ? 'dock' : 'minimize';
}
